import { supabase } from '../client';

/**
 * Cheap parity probe used by the dev-mode startup check.
 *
 * Issues a `select * limit 0` against `<schema>.<view>` and inspects the
 * error code / message. If PostgREST reports the schema isn't exposed
 * (PGRST106 / "Invalid schema"), we throw a high-signal error that the dev
 * shell renders as a hard banner so the developer sees the broken state
 * the moment they open localhost -- not when an end user clicks something
 * three pages deep.
 *
 * Permission errors (RLS denial) under an authenticated session are NOT a
 * parity failure -- they mean the schema IS exposed but the user can't
 * read those rows, which is the desired behavior.
 */
export interface SchemaParityFailure {
  kind: 'schema-not-exposed' | 'view-missing' | 'unknown';
  schema: string;
  view: string;
  message: string;
  code?: string;
}

export class SchemaParityError extends Error {
  readonly failure: SchemaParityFailure;
  constructor(failure: SchemaParityFailure) {
    super(failure.message);
    this.name = 'SchemaParityError';
    this.failure = failure;
  }
}

/**
 * Probe whether `<schema>.<view>` is reachable through PostgREST.
 *
 * Returns silently on success (including on RLS denial -- that proves the
 * schema is wired up). Throws `SchemaParityError` only when the API gateway
 * itself refuses to route to the schema.
 */
export async function probeSchemaIsExposed(schema: string, view: string): Promise<void> {
  // The Database generic on the client narrows `.schema()` to known schemas.
  // We cast to `never` because the probe is intentionally generic over any
  // module schema name -- type safety here would defeat the purpose.
  const { error } = await supabase()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .schema(schema as any)
    .from(view)
    .select('*')
    .limit(0);

  if (!error) return;

  const code = (error as { code?: string }).code;
  const msg = error.message ?? '';

  // PGRST106 is the canonical "schema not in db-schemas" code.
  if (code === 'PGRST106' || /invalid schema/i.test(msg)) {
    throw new SchemaParityError({
      kind: 'schema-not-exposed',
      schema,
      view,
      code,
      message:
        `Module schema "${schema}" is not reachable through PostgREST. ` +
        `Apply the migration that creates it (see supabase/migrations/<ts>_${schema}_init.sql) ` +
        `and confirm it calls select core.fn_expose_schema('${schema}'). ` +
        `Original: ${msg}`,
    });
  }

  // 42P01 = relation does not exist. Means the schema is exposed but the
  // promised v_* view is missing.
  if (code === '42P01' || /relation .* does not exist/i.test(msg)) {
    throw new SchemaParityError({
      kind: 'view-missing',
      schema,
      view,
      code,
      message:
        `Schema "${schema}" is exposed but the parity view "${view}" is missing. ` +
        `Either restore the v_* view in the module's migration or update the ` +
        `module's healthProbe to point at an existing read model. Original: ${msg}`,
    });
  }

  // RLS denial / auth errors are FINE -- they prove the route works.
  // Anything else is unknown and we surface it as a failure to be safe.
  if (code === 'PGRST301' || code === '42501' || /permission denied/i.test(msg)) {
    return; // schema exposed, user just can't see rows. That's expected.
  }

  throw new SchemaParityError({
    kind: 'unknown',
    schema,
    view,
    code,
    message: `Probe ${schema}.${view} failed unexpectedly: ${msg} (code=${code ?? 'n/a'})`,
  });
}
